import React, { useState, useEffect, useMemo } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import {
	Typography,
	Input,
	Textarea,
	Select,
	MenuItem,
	MultiSelect,
	Button,
	Chip,
} from "@/components/ui";
import {
	FaBell,
	FaSave,
	FaFileAlt,
	FaChartBar,
	FaExchangeAlt,
	FaRobot,
	FaCode,
	FaArrowLeft,
	FaArrowRight,
	FaCheck,
	FaClock,
	FaBellSlash,
} from "react-icons/fa";
import { useChannels } from "@/api/channel";
import { useAlertsStore, useDashboardsStore } from "@/hooks";
import useNotif from "@/hooks/use-notif";
import useTranslation from "@/hooks/use-translation";
import { Alert, AlertCreate, AlertConfig, AlertType } from "@/models/alert";

interface AlertFormModalProps {
	open: boolean;
	onClose: () => void;
	editingAlert?: Alert | null;
}

// Configuration des types d'alertes
const ALERT_TYPES: {
	value: AlertType;
	labelKey: string;
	descriptionKey: string;
	icon: React.ReactNode;
	colorClass: string;
	bgClass: string;
}[] = [
	{
		value: "new_documents",
		labelKey: "alert_type_new_documents",
		descriptionKey: "alert_type_new_documents_desc",
		icon: <FaFileAlt className="text-2xl" />,
		colorClass: "text-blue-500",
		bgClass: "bg-blue-500/10 border-blue-500/30",
	},
	{
		value: "threshold",
		labelKey: "alert_type_threshold",
		descriptionKey: "alert_type_threshold_desc",
		icon: <FaChartBar className="text-2xl" />,
		colorClass: "text-emerald-500",
		bgClass: "bg-emerald-500/10 border-emerald-500/30",
	},
	{
		value: "change",
		labelKey: "alert_type_change",
		descriptionKey: "alert_type_change_desc",
		icon: <FaExchangeAlt className="text-2xl" />,
		colorClass: "text-amber-500",
		bgClass: "bg-amber-500/10 border-amber-500/30",
	},
	{
		value: "anomaly",
		labelKey: "alert_type_anomaly",
		descriptionKey: "alert_type_anomaly_desc",
		icon: <FaRobot className="text-2xl" />,
		colorClass: "text-purple-500",
		bgClass: "bg-purple-500/10 border-purple-500/30",
	},
	{
		value: "custom",
		labelKey: "alert_type_custom",
		descriptionKey: "alert_type_custom_desc",
		icon: <FaCode className="text-2xl" />,
		colorClass: "text-rose-500",
		bgClass: "bg-rose-500/10 border-rose-500/30",
	},
];

// Intervalles prédéfinis
const INTERVAL_OPTIONS = [
	{ value: 5, labelKey: "interval_5min" },
	{ value: 15, labelKey: "interval_15min" },
	{ value: 30, labelKey: "interval_30min" },
	{ value: 60, labelKey: "interval_1h" },
	{ value: 360, labelKey: "interval_6h" },
	{ value: 720, labelKey: "interval_12h" },
	{ value: 1440, labelKey: "interval_24h" },
];

const COOLDOWN_OPTIONS = [
	{ value: 0, labelKey: "cooldown_none" },
	{ value: 60, labelKey: "cooldown_1h" },
	{ value: 360, labelKey: "cooldown_6h" },
	{ value: 720, labelKey: "cooldown_12h" },
	{ value: 1440, labelKey: "cooldown_24h" },
	{ value: 4320, labelKey: "cooldown_3d" },
	{ value: 10080, labelKey: "cooldown_7d" },
];

const AlertFormModal: React.FC<AlertFormModalProps> = ({
	open,
	onClose,
	editingAlert,
}) => {
	const { t } = useTranslation();
	const { resources: alerts, create: createAlert, update: updateAlert, refresh: refreshAlerts } = useAlertsStore();
	const { data: channels } = useChannels();
	const { resources: dashboards } = useDashboardsStore();
	const { addError, addSuccess } = useNotif();

	// Wizard step
	const [currentStep, setCurrentStep] = useState(0);
	const STEPS = [
		{ key: "type", labelKey: "step_type", icon: <FaBell /> },
		{ key: "config", labelKey: "step_config", icon: <FaChartBar /> },
		{ key: "targets", labelKey: "step_targets", icon: <FaFileAlt /> },
		{ key: "notifications", labelKey: "step_notifications", icon: <FaBell /> },
		{ key: "schedule", labelKey: "step_schedule", icon: <FaClock /> },
	];

	// Form state
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [alertType, setAlertType] = useState<AlertType>("new_documents");
	const [value, setValue] = useState<number>(1);
	const [operator, setOperator] = useState<AlertConfig["operator"]>(">");
	const [field, setField] = useState<string>("");
	const [aggregation, setAggregation] = useState<AlertConfig["aggregation"]>("count");
	const [comparePeriod, setComparePeriod] = useState<number>(1440);
	const [sensitivity, setSensitivity] = useState<number>(0.5);
	const [esQuery, setEsQuery] = useState<string>("");
	const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
	const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
	const [checkInterval, setCheckInterval] = useState<number>(60);
	const [cooldown, setCooldown] = useState<number>(1440);
	const [enabled, setEnabled] = useState(true);

	// Reset form when modal opens/closes or editingAlert changes
	useEffect(() => {
		if (open) {
			setCurrentStep(0);
			if (editingAlert) {
				setName(editingAlert.name);
				setDescription(editingAlert.description || "");
				setAlertType(editingAlert.config.type);
				setValue(editingAlert.config.value || 1);
				setOperator(editingAlert.config.operator || ">");
				setField(editingAlert.config.field || "");
				setAggregation(editingAlert.config.aggregation || "count");
				setComparePeriod(editingAlert.config.compare_period || 1440);
				setSensitivity(editingAlert.config.sensitivity || 0.5);
				setEsQuery(
					editingAlert.config.es_query
						? JSON.stringify(editingAlert.config.es_query, null, 2)
						: "",
				);
				setSelectedDashboards(editingAlert.dashboards);
				setSelectedChannels(editingAlert.channels);
				setCheckInterval(editingAlert.check_interval);
				setCooldown(editingAlert.cooldown);
				setEnabled(editingAlert.enabled);
			} else {
				// Reset to defaults
				setName("");
				setDescription("");
				setAlertType("new_documents");
				setValue(1);
				setOperator(">");
				setField("");
				setAggregation("count");
				setComparePeriod(1440);
				setSensitivity(0.5);
				setEsQuery("");
				setSelectedDashboards([]);
				setSelectedChannels([]);
				setCheckInterval(60);
				setCooldown(1440);
				setEnabled(true);
			}
		}
	}, [open, editingAlert]);

	// Validation per step
	const isStepValid = useMemo(() => {
		switch (currentStep) {
			case 0: // Type selection
				return !!alertType;
			case 1: // Configuration
				if (!name.trim()) return false;
				if (alertType === "custom") {
					try {
						if (esQuery) JSON.parse(esQuery);
						return true;
					} catch {
						return false;
					}
				}
				return true;
			case 2: // Targets (dashboards)
				return selectedDashboards.length > 0;
			case 3: // Notifications (channels)
				return selectedChannels.length > 0;
			case 4: // Schedule
				return checkInterval > 0 && cooldown >= 0;
			default:
				return true;
		}
	}, [
		currentStep,
		alertType,
		name,
		esQuery,
		selectedDashboards,
		selectedChannels,
		checkInterval,
		cooldown,
	]);

	const handleNext = () => {
		if (currentStep < STEPS.length - 1 && isStepValid) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleSubmit = async () => {
		// Validate name uniqueness
		const duplicateName = alerts?.find(
			(a) => a.name === name.trim() && a.id !== editingAlert?.id,
		);
		if (duplicateName) {
			addError(t("duplicatename") || "Un nom d'alerte identique existe déjà");
			return;
		}

		// Build config based on type
		const config: AlertConfig = {
			type: alertType,
			value: alertType !== "custom" ? value : undefined,
		};

		if (alertType === "threshold") {
			config.operator = operator;
			if (aggregation !== "count") {
				config.field = field;
				config.aggregation = aggregation;
			} else {
				config.aggregation = "count";
			}
		}

		if (alertType === "change") {
			config.compare_period = comparePeriod;
			if (aggregation !== "count") {
				config.field = field;
				config.aggregation = aggregation;
			}
		}

		if (alertType === "anomaly") {
			config.sensitivity = sensitivity;
			if (aggregation !== "count") {
				config.field = field;
				config.aggregation = aggregation;
			}
		}

		if (alertType === "custom" && esQuery) {
			try {
				config.es_query = JSON.parse(esQuery);
			} catch {
				addError(t("invalid_json") || "JSON invalide");
				return;
			}
		}

		const alertData: AlertCreate = {
			name: name.trim(),
			description: description.trim() || undefined,
			config,
			dashboards: selectedDashboards,
			channels: selectedChannels,
			check_interval: checkInterval,
			cooldown,
			enabled,
		};

		try {
			if (editingAlert?.id) {
				await updateAlert(editingAlert.id, alertData as Partial<Alert>);
				addSuccess(t("alertupdated") || "Alerte mise à jour");
			} else {
				await createAlert(alertData);
				addSuccess(t("alertcreated") || "Alerte créée");
			}
			onClose();
		} catch (error) {
			addError(t("error500") || "Erreur");
		}
	};

	const isLoading = false; // Store hooks handle loading state internally
	const isLastStep = currentStep === STEPS.length - 1;

	// Render step content
	const renderStepContent = () => {
		switch (currentStep) {
			case 0:
				return (
					<div className="space-y-4">
						<Typography variant="h6" className="text-content-primary mb-4">
							{t("select_alert_type") || "Choisissez le type d'alerte"}
						</Typography>
						<div className="grid grid-cols-1 gap-3">
							{ALERT_TYPES.map((type) => (
								<button
									key={type.value}
									type="button"
									onClick={() => setAlertType(type.value)}
									className={`
										p-4 rounded-xl border-2 text-left transition-all
										${
											alertType === type.value
												? `${type.bgClass} border-2 shadow-md scale-[1.02]`
												: "border-arc-border hover:border-content-muted bg-surface-primary"
										}
									`}
								>
									<div className="flex items-center gap-4">
										<div
											className={`
												p-3 rounded-lg ${type.bgClass} ${type.colorClass}
											`}
										>
											{type.icon}
										</div>
										<div className="flex-1">
											<Typography
												variant="h6"
												className={`${alertType === type.value ? type.colorClass : "text-content-primary"}`}
											>
												{t(type.labelKey) || type.labelKey}
											</Typography>
											<Typography
												variant="text-sm"
												className="text-content-secondary mt-1"
											>
												{t(type.descriptionKey) || type.descriptionKey}
											</Typography>
										</div>
										{alertType === type.value && (
											<FaCheck className={`${type.colorClass} text-xl`} />
										)}
									</div>
								</button>
							))}
						</div>
					</div>
				);

			case 1:
				return (
					<div className="space-y-6">
						{/* Name & Description */}
						<div className="space-y-4">
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								label={t("alert_name") || "Nom de l'alerte"}
								placeholder={t("alert_name_placeholder") || "Ex: Pic de mentions"}
								required
							/>
							<Textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								label={t("description") || "Description (optionnel)"}
								placeholder={
									t("alert_description_placeholder") ||
									"Décrivez le but de cette alerte..."
								}
								rows={2}
							/>
						</div>

						{/* Type-specific configuration */}
						<div className="p-4 rounded-xl bg-surface-secondary border border-arc-border">
							<Typography variant="h6" className="text-content-primary mb-4">
								{t("alert_condition") || "Condition de déclenchement"}
							</Typography>

							{alertType === "new_documents" && (
								<div className="space-y-4">
									<Typography variant="text-sm" className="text-content-secondary">
										{t("new_documents_desc") ||
											"L'alerte se déclenchera lorsque le nombre de documents dépasse le seuil."}
									</Typography>
									<Input
										type="number"
										value={String(value)}
										onChange={(e) => setValue(Number.parseInt(e.target.value) || 1)}
										label={t("min_documents") || "Nombre minimum de documents"}
										min={1}
									/>
								</div>
							)}

							{alertType === "threshold" && (
								<div className="space-y-4">
									<Typography variant="text-sm" className="text-content-secondary">
										{t("threshold_desc") ||
											"L'alerte se déclenchera lorsque la métrique atteint le seuil."}
									</Typography>
									<div className="grid grid-cols-2 gap-4">
										<Select
											value={aggregation}
											onChange={(val) =>
												setAggregation(val as AlertConfig["aggregation"])
											}
											label={t("aggregation") || "Agrégation"}
										>
											<MenuItem value="count">{t("count") || "Nombre"}</MenuItem>
											<MenuItem value="avg">{t("average") || "Moyenne"}</MenuItem>
											<MenuItem value="sum">{t("sum") || "Somme"}</MenuItem>
											<MenuItem value="min">{t("minimum") || "Minimum"}</MenuItem>
											<MenuItem value="max">{t("maximum") || "Maximum"}</MenuItem>
										</Select>
										<Select
											value={operator}
											onChange={(val) =>
												setOperator(val as AlertConfig["operator"])
											}
											label={t("operator") || "Opérateur"}
										>
											<MenuItem value=">">&gt; Supérieur</MenuItem>
											<MenuItem value="<">&lt; Inférieur</MenuItem>
											<MenuItem value=">=">&ge; Supérieur ou égal</MenuItem>
											<MenuItem value="<=">&le; Inférieur ou égal</MenuItem>
											<MenuItem value="==">= Égal</MenuItem>
										</Select>
									</div>
									{aggregation !== "count" && (
										<Input
											value={field}
											onChange={(e) => setField(e.target.value)}
											label={t("field") || "Champ"}
											placeholder={t("field_placeholder") || "ex: sentiment_score"}
										/>
									)}
									<Input
										type="number"
										value={String(value)}
										onChange={(e) =>
											setValue(Number.parseFloat(e.target.value) || 0)
										}
										label={t("threshold_value") || "Valeur seuil"}
									/>
								</div>
							)}

							{alertType === "change" && (
								<div className="space-y-4">
									<Typography variant="text-sm" className="text-content-secondary">
										{t("change_desc") ||
											"L'alerte se déclenchera lors d'un changement significatif."}
									</Typography>
									<div className="grid grid-cols-2 gap-4">
										<Input
											type="number"
											value={String(value)}
											onChange={(e) =>
												setValue(Number.parseFloat(e.target.value) || 10)
											}
											label={t("change_percentage") || "Pourcentage de changement (%)"}
											min={0}
											max={100}
										/>
										<Select
											value={String(comparePeriod)}
											onChange={(val) => setComparePeriod(Number(val))}
											label={t("compare_period") || "Période de comparaison"}
										>
											<MenuItem value="60">1 heure</MenuItem>
											<MenuItem value="360">6 heures</MenuItem>
											<MenuItem value="1440">24 heures</MenuItem>
											<MenuItem value="4320">3 jours</MenuItem>
											<MenuItem value="10080">7 jours</MenuItem>
										</Select>
									</div>
									<Select
										value={aggregation}
										onChange={(val) =>
											setAggregation(val as AlertConfig["aggregation"])
										}
										label={t("metric") || "Métrique à comparer"}
									>
										<MenuItem value="count">{t("document_count") || "Nombre de documents"}</MenuItem>
										<MenuItem value="avg">{t("average") || "Moyenne"}</MenuItem>
										<MenuItem value="sum">{t("sum") || "Somme"}</MenuItem>
									</Select>
									{aggregation !== "count" && (
										<Input
											value={field}
											onChange={(e) => setField(e.target.value)}
											label={t("field") || "Champ"}
											placeholder={t("field_placeholder") || "ex: engagement_rate"}
										/>
									)}
								</div>
							)}

							{alertType === "anomaly" && (
								<div className="space-y-4">
									<Typography variant="text-sm" className="text-content-secondary">
										{t("anomaly_desc") ||
											"L'alerte détectera automatiquement les comportements anormaux."}
									</Typography>
									<div>
										<Typography variant="text-sm" className="text-content-primary mb-2">
											{t("sensitivity") || "Sensibilité"}: {Math.round(sensitivity * 100)}%
										</Typography>
										<input
											type="range"
											min="0"
											max="1"
											step="0.1"
											value={sensitivity}
											onChange={(e) => setSensitivity(Number.parseFloat(e.target.value))}
											className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-surface-secondary"
										/>
										<div className="flex justify-between text-xs text-content-tertiary mt-1">
											<span>{t("less_alerts") || "Moins d'alertes"}</span>
											<span>{t("more_alerts") || "Plus d'alertes"}</span>
										</div>
									</div>
									<Select
										value={aggregation}
										onChange={(val) =>
											setAggregation(val as AlertConfig["aggregation"])
										}
										label={t("metric_to_monitor") || "Métrique à surveiller"}
									>
										<MenuItem value="count">{t("document_count") || "Nombre de documents"}</MenuItem>
										<MenuItem value="avg">{t("average") || "Moyenne"}</MenuItem>
										<MenuItem value="sum">{t("sum") || "Somme"}</MenuItem>
									</Select>
									{aggregation !== "count" && (
										<Input
											value={field}
											onChange={(e) => setField(e.target.value)}
											label={t("field") || "Champ"}
											placeholder={t("field_placeholder") || "ex: response_time"}
										/>
									)}
								</div>
							)}

							{alertType === "custom" && (
								<div className="space-y-4">
									<Typography variant="text-sm" className="text-content-secondary">
										{t("custom_desc") ||
											"Définissez une requête Elasticsearch personnalisée."}
									</Typography>
									<Input
										type="number"
										value={String(value)}
										onChange={(e) =>
											setValue(Number.parseFloat(e.target.value) || 1)
										}
										label={t("threshold_value") || "Seuil de déclenchement"}
									/>
									<Textarea
										value={esQuery}
										onChange={(e) => setEsQuery(e.target.value)}
										label={t("es_query") || "Requête Elasticsearch (JSON)"}
										placeholder='{"size": 0, "aggs": {...}}'
										rows={8}
										className="font-mono text-sm"
									/>
								</div>
							)}
						</div>
					</div>
				);

			case 2:
				return (
					<div className="space-y-4">
						<Typography variant="h6" className="text-content-primary mb-2">
							{t("select_dashboards") || "Sélectionnez les dashboards à surveiller"}
						</Typography>
						<Typography variant="text-sm" className="text-content-secondary mb-4">
							{t("dashboards_desc") ||
								"L'alerte sera évaluée sur les données de ces dashboards."}
						</Typography>
						<MultiSelect
							value={selectedDashboards}
							onChange={setSelectedDashboards}
							options={
								dashboards?.map((d) => ({
									value: d.id,
									label: d.name || "Dashboard",
								})) || []
							}
							label={t("dashboards") || "Dashboards"}
							placeholder={t("select_dashboards_placeholder") || "Choisir des dashboards..."}
						/>
						{selectedDashboards.length > 0 && (
							<div className="flex flex-wrap gap-2 mt-4">
								{selectedDashboards.map((id) => {
									const dashboard = dashboards?.find((d) => d.id === id);
									return (
										<Chip key={id} size="sm" variant="soft" color="info">
											{dashboard?.name || id}
										</Chip>
									);
								})}
							</div>
						)}
					</div>
				);

			case 3:
				return (
					<div className="space-y-4">
						<Typography variant="h6" className="text-content-primary mb-2">
							{t("select_channels") || "Canaux de notification"}
						</Typography>
						<Typography variant="text-sm" className="text-content-secondary mb-4">
							{t("channels_desc") ||
								"Choisissez comment vous souhaitez être notifié."}
						</Typography>
						<MultiSelect
							value={selectedChannels}
							onChange={setSelectedChannels}
							options={
								channels?.map((c) => ({
									value: c.id,
									label: `${c.name} (${c.doc_type})`,
								})) || []
							}
							label={t("channels") || "Canaux"}
							placeholder={t("select_channels_placeholder") || "Choisir des canaux..."}
						/>
						{selectedChannels.length > 0 && (
							<div className="flex flex-wrap gap-2 mt-4">
								{selectedChannels.map((id) => {
									const channel = channels?.find((c) => c.id === id);
									return (
										<Chip key={id} size="sm" variant="soft">
											{channel?.name || id}
										</Chip>
									);
								})}
							</div>
						)}
						{channels?.length === 0 && (
							<div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
								<Typography variant="text-sm" className="text-amber-600">
									{t("no_channels_warning") ||
										"Aucun canal configuré. Créez d'abord un canal dans les paramètres."}
								</Typography>
							</div>
						)}
					</div>
				);

			case 4:
				return (
					<div className="space-y-6">
						<Typography variant="h6" className="text-content-primary mb-2">
							{t("schedule_settings") || "Paramètres de planification"}
						</Typography>

						{/* Check interval */}
						<div className="p-4 rounded-xl bg-surface-secondary border border-arc-border">
							<div className="flex items-center gap-3 mb-4">
								<div className="p-2 rounded-lg bg-accent/10 text-accent">
									<FaClock />
								</div>
								<div>
									<Typography variant="h6" className="text-content-primary">
										{t("check_interval") || "Intervalle de vérification"}
									</Typography>
									<Typography variant="text-sm" className="text-content-tertiary">
										{t("check_interval_desc") ||
											"À quelle fréquence l'alerte doit-elle être évaluée ?"}
									</Typography>
								</div>
							</div>
							<div className="flex flex-wrap gap-2">
								{INTERVAL_OPTIONS.map((opt) => (
									<button
										key={opt.value}
										type="button"
										onClick={() => setCheckInterval(opt.value)}
										className={`
											px-4 py-2 rounded-lg border transition-all
											${
												checkInterval === opt.value
													? "bg-accent text-white border-accent"
													: "bg-surface-primary border-arc-border hover:border-accent text-content-primary"
											}
										`}
									>
										{t(opt.labelKey) || `${opt.value} min`}
									</button>
								))}
							</div>
						</div>

						{/* Cooldown */}
						<div className="p-4 rounded-xl bg-surface-secondary border border-arc-border">
							<div className="flex items-center gap-3 mb-4">
								<div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
									<FaBellSlash />
								</div>
								<div>
									<Typography variant="h6" className="text-content-primary">
										{t("cooldown") || "Période de silence"}
									</Typography>
									<Typography variant="text-sm" className="text-content-tertiary">
										{t("cooldown_desc") ||
											"Temps minimum entre deux déclenchements."}
									</Typography>
								</div>
							</div>
							<div className="flex flex-wrap gap-2">
								{COOLDOWN_OPTIONS.map((opt) => (
									<button
										key={opt.value}
										type="button"
										onClick={() => setCooldown(opt.value)}
										className={`
											px-4 py-2 rounded-lg border transition-all
											${
												cooldown === opt.value
													? "bg-amber-500 text-white border-amber-500"
													: "bg-surface-primary border-arc-border hover:border-amber-500 text-content-primary"
											}
										`}
									>
										{t(opt.labelKey) || (opt.value === 0 ? "Aucun" : `${opt.value} min`)}
									</button>
								))}
							</div>
						</div>

						{/* Summary */}
						<div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
							<Typography variant="h6" className="text-accent mb-3">
								{t("summary") || "Résumé"}
							</Typography>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-content-tertiary">{t("alert_name") || "Nom"}:</span>
									<span className="text-content-primary font-medium">{name || "-"}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-content-tertiary">{t("type") || "Type"}:</span>
									<span className="text-content-primary font-medium">
										{t(`alert_type_${alertType}`) || alertType}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-content-tertiary">{t("dashboards") || "Dashboards"}:</span>
									<span className="text-content-primary font-medium">
										{selectedDashboards.length}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-content-tertiary">{t("channels") || "Canaux"}:</span>
									<span className="text-content-primary font-medium">
										{selectedChannels.length}
									</span>
								</div>
							</div>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<BaseModal
			open={open}
			onClose={onClose}
			icon={<FaBell />}
			title={
				editingAlert?.id
					? t("editalert") || "Modifier l'alerte"
					: t("createalert") || "Créer une alerte"
			}
			variant="accent"
			size="lg"
			customFooter={
				<div className="flex items-center justify-between w-full">
					{/* Step indicators */}
					<div className="flex items-center gap-2">
						{STEPS.map((step, index) => (
							<div
								key={step.key}
								className={`
									w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
									${
										index === currentStep
											? "bg-accent text-white"
											: index < currentStep
												? "bg-emerald-500 text-white"
												: "bg-surface-secondary text-content-muted"
									}
								`}
							>
								{index < currentStep ? <FaCheck className="text-xs" /> : index + 1}
							</div>
						))}
					</div>

					{/* Navigation buttons */}
					<div className="flex gap-2">
						{currentStep > 0 && (
							<Button variant="ghost" onClick={handleBack} leftIcon={<FaArrowLeft />}>
								{t("back") || "Retour"}
							</Button>
						)}
						{!isLastStep ? (
							<Button
								variant="primary"
								onClick={handleNext}
								disabled={!isStepValid}
								rightIcon={<FaArrowRight />}
							>
								{t("next") || "Suivant"}
							</Button>
						) : (
							<Button
								variant="primary"
								onClick={handleSubmit}
								disabled={!isStepValid || isLoading}
								loading={isLoading}
								leftIcon={<FaSave />}
							>
								{editingAlert?.id
									? t("update") || "Mettre à jour"
									: t("create") || "Créer"}
							</Button>
						)}
					</div>
				</div>
			}
		>
			<div className="min-h-[400px]">{renderStepContent()}</div>
		</BaseModal>
	);
};

export default AlertFormModal;
